import { type Params, type Path, useNavigate, useParams } from '@exam-center/router';
import { Tab, TabContext, TabList } from '@shared/components';
import { useMemo } from 'react';
import { matchPath, useLocation } from 'react-router-dom';

export interface NavigationTabItem {
  value: string;
  label: string;
  /**
   * If disableCondition is true, the tab will be disabled.
   */
  disabled?: boolean;
  path: Path;
  requiredPermission?: string[];
}

export interface NavigationTabsProps {
  items: NavigationTabItem[];
  shouldReplace?: boolean;
}

export default function NavigationTabs(props: Readonly<NavigationTabsProps>) {
  const { items, shouldReplace = false } = props;
  const location = useLocation();
  const navigate = useNavigate();

  const params = useParams(location.pathname as keyof Params);

  const currentTabValue = useMemo(() => {
    const locationPathname = location.pathname;

    const matchTab = items.find((tab) => {
      return matchPath(tab.path, locationPathname);
    });

    return matchTab?.value ?? items?.[0]?.value;
  }, [location]);

  const handleNavigate = (path: Path, shouldReplace: boolean) => {
    if (location.pathname === path) {
      return;
    }

    // @ts-expect-error Navigate to path with params has some typing issues
    navigate(path, { params, replace: shouldReplace });
  };

  return (
    <TabContext value={currentTabValue}>
      <TabList variant='scrollable'>
        {items.map((tab) => {
          const shouldDisable = !tab.path || tab.disabled;
          return (
            <Tab
              data-testid={tab.value}
              key={tab.value}
              label={tab.label}
              value={tab.value}
              disabled={shouldDisable}
              onClick={() => handleNavigate(tab.path, shouldReplace)}
            />
          );
        })}
      </TabList>
    </TabContext>
  );
}
